import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../../api';

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 460px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const CloseBtn = styled.button`
  float: right;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  &:hover { color: #333; }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  border-bottom: 2px solid #E5E7EB;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${p => p.active ? '#0010ff' : '#9CA3AF'};
  border-bottom: 2px solid ${p => p.active ? '#0010ff' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 5px;
  }
  input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 14px;
    box-sizing: border-box;
    &:focus { outline: none; border-color: #0010ff; }
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 12px;
  background: #0010ff;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;
  &:hover { background: #0008cc; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorMsg = styled.div`
  background: #FEE2E2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 10px 14px;
  color: #DC2626;
  font-size: 13px;
  margin-bottom: 16px;
`;

const CheckboxRow = styled.div`
  margin-top: 12px;
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #555;
  a { color: #0010ff; }
`;

const SDOAuthModal = ({ isOpen, onClose, redirectTo, refreshUser }) => {
  const { i18n } = useTranslation('common');
  const fr = i18n.language === 'fr';

  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup fields
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [societe, setSociete] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  if (!isOpen) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.Login({ email: loginEmail, password: loginPassword });
      if (refreshUser) await refreshUser();
      if (redirectTo) window.location.href = redirectTo;
    } catch (err) {
      setError(err.response?.data?.error || (fr ? "Email ou mot de passe incorrect" : "Invalid email or password"));
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== passwordConfirm) {
      setError(fr ? "Les mots de passe ne correspondent pas" : "Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await api.RegisterInstitutionnel({
        email,
        password,
        firstname,
        lastname,
        societe,
        phone: '',
        language: i18n.language
      });
      if (refreshUser) await refreshUser();
      if (redirectTo) window.location.href = redirectTo;
    } catch (err) {
      setError(err.response?.data?.error || (fr ? "Erreur lors de l'inscription" : "Registration error"));
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <CloseBtn onClick={onClose}>&times;</CloseBtn>
        <Title>{fr ? 'Accéder au formulaire' : 'Access the form'}</Title>
        <Subtitle>{fr ? 'Connectez-vous ou créez un compte pour continuer' : 'Log in or create an account to continue'}</Subtitle>

        <Tabs>
          <Tab active={tab === 'login'} onClick={() => { setTab('login'); setError(null); }}>
            {fr ? 'Se connecter' : 'Log in'}
          </Tab>
          <Tab active={tab === 'signup'} onClick={() => { setTab('signup'); setError(null); }}>
            {fr ? 'Créer un compte' : 'Sign up'}
          </Tab>
        </Tabs>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        {tab === 'login' ? (
          <form onSubmit={handleLogin}>
            <FormGroup>
              <label>Email</label>
              <input type="email" required value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="email@example.com" />
            </FormGroup>
            <FormGroup>
              <label>{fr ? 'Mot de passe' : 'Password'}</label>
              <input type="password" required value={loginPassword} onChange={e => setLoginPassword(e.target.value)} />
            </FormGroup>
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? (fr ? 'Connexion...' : 'Logging in...') : (fr ? 'Se connecter' : 'Log in')}
            </SubmitBtn>
          </form>
        ) : (
          <form onSubmit={handleSignup}>
            <FormGroup>
              <label>{fr ? 'Prénom *' : 'First name *'}</label>
              <input required value={firstname} onChange={e => setFirstname(e.target.value)} placeholder={fr ? 'Prénom' : 'First name'} />
            </FormGroup>
            <FormGroup>
              <label>{fr ? 'Nom *' : 'Last name *'}</label>
              <input required value={lastname} onChange={e => setLastname(e.target.value)} placeholder={fr ? 'Nom' : 'Last name'} />
            </FormGroup>
            <FormGroup>
              <label>Email *</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
            </FormGroup>
            <FormGroup>
              <label>{fr ? 'Structure / Entreprise *' : 'Company / Studio *'}</label>
              <input required value={societe} onChange={e => setSociete(e.target.value)} placeholder={fr ? 'Nom de votre structure' : 'Company name'} />
            </FormGroup>
            <FormGroup>
              <label>{fr ? 'Mot de passe *' : 'Password *'}</label>
              <input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} placeholder={fr ? 'Minimum 6 caractères' : 'Min. 6 characters'} />
            </FormGroup>
            <FormGroup>
              <label>{fr ? 'Confirmer le mot de passe *' : 'Confirm password *'}</label>
              <input type="password" required minLength={6} value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} />
            </FormGroup>
            <CheckboxRow>
              <input type="checkbox" required style={{ marginTop: 2 }} />
              <span>
                {fr ? "J'ai lu et j'accepte " : "I have read and accept "}
                <a href="/documents/cvg-cgu.pdf" target="_blank" rel="noreferrer">
                  {fr ? "les conditions générales." : "the terms and conditions."}
                </a>
              </span>
            </CheckboxRow>
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? (fr ? 'Création...' : 'Creating...') : (fr ? 'Créer mon compte' : 'Create account')}
            </SubmitBtn>
          </form>
        )}
      </Modal>
    </Overlay>
  );
};

export default SDOAuthModal;
